/**
 * Adapter map — maps FieldDef.adapterHint to the appropriate Rhf* component.
 * Self-registers all DS adapters at module init (no manual bootstrap needed).
 */

import type { ComponentType } from 'react';
import { RhfInputText } from '@packages/ui/hook-form-adapters/RhfInputText';
import { RhfInputNumber } from '@packages/ui/hook-form-adapters/RhfInputNumber';
import { RhfTextArea } from '@packages/ui/hook-form-adapters/RhfTextArea';
import { RhfSelect } from '@packages/ui/hook-form-adapters/RhfSelect';
import { RhfAsyncSelect } from '@packages/ui/hook-form-adapters/RhfAsyncSelect';
import { RhfSearchPicker } from '@packages/ui/hook-form-adapters/RhfSearchPicker';
import { RhfDatePicker } from '@packages/ui/hook-form-adapters/RhfDatePicker';
import { RhfTimePicker } from '@packages/ui/hook-form-adapters/RhfTimePicker';
import { RhfCheckbox } from '@packages/ui/hook-form-adapters/RhfCheckbox';
import { RhfToggle } from '@packages/ui/hook-form-adapters/RhfToggle';
import { RhfRadioGroup } from '@packages/ui/hook-form-adapters/RhfRadioGroup';
import { RhfFileInput } from '@packages/ui/hook-form-adapters/RhfFileInput';
import { RhfFileUpload } from '@packages/ui/hook-form-adapters/RhfFileUpload';
import { RhfDocumentList } from '@packages/ui/hook-form-adapters/RhfDocumentList';
import { RhfRichText } from '@packages/ui/hook-form-adapters/RhfRichText';
import { RhfTemplateRichText } from '@packages/ui/hook-form-adapters/RhfTemplateRichText';
import { RhfHtmlPreview } from '@packages/ui/hook-form-adapters/RhfHtmlPreview';
import { RhfOtpInput } from '@packages/ui/hook-form-adapters/RhfOtpInput';
import { RhfField } from '@packages/ui/hook-form-adapters/RhfField';
import { RhfComputed } from '@packages/ui/hook-form-adapters/RhfComputed';
import { RhfWeekDayToggle } from '@packages/ui/hook-form-adapters/RhfWeekDayToggle';
import { RhfSignature } from './RhfSignature';
import {
  RhfProseField,
  RhfLabelField,
  RhfMonacoField,
  RhfJsonField,
  RhfMultiSelectPanel,
} from './placeholderAdapters';

export type AdapterHint =
  | 'text'
  | 'password'
  | 'number'
  | 'textarea'
  | 'select'
  | 'async-select'
  | 'search-picker'
  | 'date'
  | 'datepicker'
  | 'timepicker'
  | 'checkbox'
  | 'toggle'
  | 'radio'
  | 'file'
  | 'file-upload'
  | 'richtext'
  | 'template-richtext'
  | 'html-preview'
  | 'otp'
  | 'computed'
  | 'custom'
  | 'signature'
  | 'prose-field'
  | 'monaco'
  | 'json'
  | 'label'
  | 'multi-select-panel'
  /** Generic session-store-backed chip list. Pass storeKey via adapterProps. */
  | 'document-list'
  /** Multi-select weekday chips (legacy rounded-secondary day toggles). */
  | 'weekday-toggle';

const adapterRegistry = new Map<string, ComponentType<any>>([
  ['text', RhfInputText],
  ['password', RhfInputText],
  ['masked', RhfInputText],
  ['number', RhfInputNumber],
  ['textarea', RhfTextArea],
  ['select', RhfSelect],
  ['async-select', RhfAsyncSelect],
  ['search-picker', RhfSearchPicker],
  ['date', RhfDatePicker],
  ['datepicker', RhfDatePicker],
  ['timepicker', RhfTimePicker],
  ['checkbox', RhfCheckbox],
  ['toggle', RhfToggle],
  ['radio', RhfRadioGroup],
  ['file', RhfFileInput],
  ['file-upload', RhfFileUpload],
  ['document-list', RhfDocumentList],
  ['richtext', RhfRichText],
  ['template-richtext', RhfTemplateRichText],
  ['html-preview', RhfHtmlPreview],
  ['otp', RhfOtpInput],
  ['phone', RhfInputText],
  ['computed', RhfComputed],
  ['custom', RhfField],
  ['signature', RhfSignature],
  ['prose-field', RhfProseField],
  ['monaco', RhfMonacoField],
  ['json', RhfJsonField],
  ['label', RhfLabelField],
  ['multi-select-panel', RhfMultiSelectPanel],
  ['weekday-toggle', RhfWeekDayToggle],
]);

export const AdapterMap = {
  register(hint: string, component: ComponentType<any>): void {
    adapterRegistry.set(hint, component);
  },

  get(hint: string): ComponentType<any> | undefined {
    return adapterRegistry.get(hint);
  },

  getOrDefault(hint: string | undefined): ComponentType<any> | undefined {
    if (!hint) return adapterRegistry.get('text');
    return adapterRegistry.get(hint) ?? adapterRegistry.get('text');
  },

  registerDefaults(adapters: Record<string, ComponentType<any>>): void {
    for (const [hint, component] of Object.entries(adapters)) {
      adapterRegistry.set(hint, component);
    }
  },

  clear(): void {
    adapterRegistry.clear();
  },
};
